import {
  Button,
  Divider,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { useMemo } from "react";
import { NavLink } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import {
  NavigationBlocker,
  NavigationBlockerProvider,
} from "src/components/NavigationBlocker";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import useCheckUserRole from "src/hooks/useCheckUserRole";
import { useIsSsoOrg } from "src/hooks/useIsSsoOrg";
import { useOrganization } from "src/hooks/useOrganization";
import { validateWithZod } from "src/services/formValidations";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import * as AccountLookup from "./AccountLookup";
import * as Auth0 from "./Auth0";
import * as Login from "./Login";
import * as Mfa from "./Mfa";

export const SecurityTab = () => {
  const organizationRD = useOrganization();
  const accountLookup = AccountLookup.useForm();
  const auth0 = Auth0.useForm();
  const login = Login.useForm();
  const mfa = Mfa.useForm();
  const toast = useAvelaToast();
  const { isAvelaAdmin } = useCheckUserRole();
  const isSsoOrgRD = useIsSsoOrg();

  return (
    <RemoteDataView
      remoteData={RD.toTuple(
        organizationRD,
        useMemo(() => {
          return RD.toTuple4(
            accountLookup.initialValues,
            auth0.initialValues,
            login.initialValues,
            mfa.initialValues
          ).map(
            ([
              accountLookupInitialValues,
              auth0InitialValues,
              loginInitialValues,
              mfaInitialValues,
            ]) => {
              return {
                ...accountLookupInitialValues,
                ...auth0InitialValues,
                ...loginInitialValues,
                ...mfaInitialValues,
              };
            }
          );
        }, [
          accountLookup.initialValues,
          auth0.initialValues,
          login.initialValues,
          mfa.initialValues,
        ])
      )}
      loading={<Loading />}
      error={() => <GenericError />}
    >
      {([organization, initialValues]) => {
        return (
          <NavigationBlockerProvider>
            <Formik<FormikType>
              initialValues={initialValues}
              validate={validateWithZod(FormikSchema)}
              onSubmit={async (values: FormikType, formik) => {
                try {
                  await Promise.all([
                    accountLookup.submit(values),
                    auth0.submit(values),
                    login.submit(values),
                    mfa.submit(values),
                  ]);

                  toast({
                    title: "Organization updated",
                    isClosable: true,
                    status: "success",
                  });
                  formik.resetForm({ values });
                } catch (error) {
                  console.error(error);
                  toast.error({
                    title: "Error editing organization",
                    description:
                      error instanceof Error && error.message
                        ? error.message
                        : "Oops! Something went wrong. Please try again later or report the problem to our support team",
                  });
                }
              }}
            >
              {(formik) => {
                return (
                  <Flex
                    as={Form}
                    direction="column"
                    height="100%"
                    grow="1"
                    gap={6}
                  >
                    <Heading as="h2" variant="admin">
                      Security
                    </Heading>
                    <AccountLookup.Form />

                    <Flex direction="column">
                      <Heading as="h3" variant="admin">
                        Access & policies
                      </Heading>
                      <Text color="blackAlpha.700" fontSize="sm">
                        Configure access policies for the organization.
                      </Text>
                    </Flex>
                    {isAvelaAdmin ? (
                      <Flex align="center">
                        <Button
                          as={NavLink}
                          colorScheme="gray"
                          to={Url.OrgAdmin.Org.editPolicy(organization)}
                          aria-label={`Edit access policies`}
                          variant="outline"
                        >
                          Edit access policies
                        </Button>
                        <Spacer />
                      </Flex>
                    ) : null}
                    <Mfa.Form isSsoOrgRemoteData={isSsoOrgRD} />

                    {isAvelaAdmin ? (
                      <>
                        <Divider />
                        <Heading as="h2" variant="admin">
                          For developer use only!
                        </Heading>
                        <Auth0.Form />
                        <Login.Form />
                      </>
                    ) : null}
                    <Spacer />
                    <AdminFormButtons insideTabPanel>
                      <Spacer />
                      <Button
                        type="submit"
                        isLoading={formik.isSubmitting}
                        isDisabled={!formik.dirty || !formik.isValid}
                      >
                        Update
                      </Button>
                      <NavigationBlocker hasChanges={formik.dirty} />
                    </AdminFormButtons>
                  </Flex>
                );
              }}
            </Formik>
          </NavigationBlockerProvider>
        );
      }}
    </RemoteDataView>
  );
};

const Loading = () => {
  return (
    <Flex direction="column" gap={6}>
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Spacer height="1rem" />
      <Skeleton height="2rem" width="15rem" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
      <Skeleton height="4rem" width="100%" />
    </Flex>
  );
};

const FormikSchema = AccountLookup.FormikSchema.merge(Auth0.FormikSchema)
  .merge(Login.FormikSchema)
  .merge(Mfa.FormikSchema);
type FormikType = z.infer<typeof FormikSchema>;
